import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import apiClient from '../api/api';
import toast from 'react-hot-toast';
import { getOptimizedCloudinaryUrl } from '../utils/cloudinaryUtils';
import { getSocket } from '../socketInstance';
import { getMediaUrl } from '../utils/imageUtils';
import { useAuthStore } from './useAuthStore';
import { useInteractionStore } from './useInteractionStore';

const optimizePost = (post) => ({
  ...post,
  author: {
    ...post.author,
    avatar: getOptimizedCloudinaryUrl(post.author.avatar, {
      width: 48,
      height: 48,
      crop: 'thumb',
    }),
  },
  originalPost: post.originalPost ? optimizePost(post.originalPost) : undefined,
});

const optimizeStory = (story) => ({
  ...story,
  author: {
    ...story.author,
    avatar: getOptimizedCloudinaryUrl(story.author.avatar, {
      width: 56,
      height: 56,
      crop: 'thumb',
    }),
  },
  scenes: story.scenes.map((scene) => ({
    ...scene,
    mediaUrl: getOptimizedCloudinaryUrl(scene.mediaUrl, { width: 600 }),
    textElements: scene.textElements || [],
    emojiElements: scene.emojiElements || [],
  })),
});

const normalizeMediaObject = (media) => {
  if (!media) return null;
  if (typeof media === 'string') return { url: media, publicId: null };
  if (typeof media === 'object') {
    const url = media.url || media.path || media.secure_url || media.mediaUrl || '';
    if (!url) return null;
    return {
      url,
      publicId: media.publicId || media.public_id || media.id || null,
    };
  }
  return null;
};

const resolveMediaUrl = (media) => normalizeMediaObject(media)?.url || '';

const initialState = {
  articles: {
    relatedCache: {},
    navigationCache: {},
    articlesCache: {},
    news: { docs: [], page: 1, totalPages: 1, totalDocs: 0 },
    isLoading: false,
    error: null,
  },
  feed: {
    posts: [],
    hybridItems: [],
    wallPosts: {},
    isLoading: true,
    page: 1,
    hasMore: true,
    authUserId: null,
    authUsername: null,
    authUserAvatar: null,
  },
  stories: {
    stories: [],
    archivedStories: [],
    isLoading: false,
    isLoadingArchived: false,
  },
  jobs: {
    jobs: [],
    currentJob: null,
    isLoading: false,
    isApplying: false,
    error: null,
  },
  polls: {
    polls: [],
    selectedPoll: null,
    isLoading: false,
    error: null,
    userVotes: {},
  },
  surveys: {
    surveyCache: {},
    isLoading: false,
    isSubmitting: false,
    error: null,
  },
  publicAds: {
    ads: [],
    isLoading: false,
    error: null,
  },
  defaultAssets: {
    assets: null,
    isLoading: false,
    error: null,
  },
  userAds: {
    myAds: [],
    isLoading: false,
    error: null,
  },
  categories: {
    categories: [],
    isLoading: true,
    error: null,
  },
  stores: {
    stores: [],
    storesCache: {},
    isLoading: false,
    error: null,
  },
  bloggers: {
    bloggers: [],
    isLoading: false,
    error: null,
  },
  campaigns: {
    campaigns: [],
    isLoading: false,
    currentCampaignDetails: null,
    error: null,
  },
  company: {
    companyCache: {},
    company: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
  },
  albums: {
    isUploading: false,
    uploadProgress: 0,
    collections: [],
    isLoading: false,
  },
  clips: {
    isUploading: false,
    uploadProgress: 0,
  },
  users: {
    users: [],
    isLoading: false,
    error: null,
  },
  profiles: {
    profilesCache: {},
    isLoading: false,
    error: null,
    isUpdatingAvatar: false,
    isUpdatingCover: false,
  },
  media: {
    videoDevices: [],
    audioDevices: [],
    selectedVideoId: null,
    selectedAudioId: null,
    stream: null,
    isStreamActive: false,
    error: null,
  },
};

export const useDataStore = create(
  devtools(
    persist(
      (set, get) => {
        const setSlice = (slice, patch) =>
          set((state) => ({
            [slice]: { ...state[slice], ...patch },
          }));

        return {
          ...initialState,

          // =========================
          // Articles
          // =========================
          getArticleById: async (articleId) => {
            const cachedArticle = get().articles.articlesCache[articleId];
            if (cachedArticle) {
              return cachedArticle;
            }

            setSlice('articles', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/posts/${articleId}`);
              const article = response.data;
              set((state) => ({
                articles: {
                  ...state.articles,
                  articlesCache: { ...state.articles.articlesCache, [articleId]: article },
                  isLoading: false,
                },
              }));
              return article;
            } catch (err) {
              const message = err.response?.data?.message || 'Error al obtener el artículo.';
              setSlice('articles', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          createArticle: async (articleData) => {
            setSlice('articles', { isLoading: true, error: null });
            try {
              const response = await apiClient.post('/posts/article', articleData);
              const newArticle = response.data;
              set((state) => ({
                articles: {
                  ...state.articles,
                  articlesCache: { ...state.articles.articlesCache, [newArticle._id]: newArticle },
                  isLoading: false,
                },
              }));
              toast.success('¡Artículo guardado con éxito!');
              return newArticle;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo guardar el artículo.';
              setSlice('articles', { isLoading: false, error: message });
              toast.error(message);
              throw err;
            }
          },

          updateArticle: async (articleId, articleData) => {
            setSlice('articles', { isLoading: true, error: null });
            try {
              const response = await apiClient.patch(`/posts/${articleId}`, articleData);
              const updatedArticle = response.data;
              set((state) => ({
                articles: {
                  ...state.articles,
                  articlesCache: { ...state.articles.articlesCache, [articleId]: updatedArticle },
                  isLoading: false,
                },
              }));
              toast.success('¡Artículo actualizado con éxito!');
              return updatedArticle;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo actualizar el artículo.';
              setSlice('articles', { isLoading: false, error: message });
              toast.error(message);
              throw err;
            }
          },

          deleteArticle: async (articleId) => {
            setSlice('articles', { isLoading: true, error: null });
            try {
              await apiClient.delete(`/posts/${articleId}`);
              set((state) => {
                const newCache = { ...state.articles.articlesCache };
                delete newCache[articleId];
                return {
                  articles: {
                    ...state.articles,
                    articlesCache: newCache,
                    isLoading: false,
                  },
                };
              });
              toast.success('Artículo eliminado con éxito.');
              return true;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo eliminar el artículo.';
              setSlice('articles', { isLoading: false, error: message });
              toast.error(message);
              return false;
            }
          },

          fetchRelatedArticles: async (postId) => {
            const cachedRelated = get().articles.relatedCache[postId];
            if (cachedRelated) {
              return cachedRelated;
            }

            setSlice('articles', { isLoading: true });
            try {
              const response = await apiClient.get(`/posts/${postId}/related`);
              const related = response.data;
              set((state) => ({
                articles: {
                  ...state.articles,
                  relatedCache: { ...state.articles.relatedCache, [postId]: related },
                  isLoading: false,
                },
              }));
              return related;
            } catch (err) {
              setSlice('articles', { isLoading: false, error: 'No se pudieron cargar las noticias relacionadas.' });
              return [];
            }
          },

          fetchNews: async (page = 1, category = null, limit = 10, countryToExclude = null) => {
            setSlice('articles', { isLoading: true, error: null, news: { docs: [], page: 1, totalPages: 1, totalDocs: 0 } });
            try {
              let url = '/posts/news';
              const queryParams = new URLSearchParams();
              queryParams.append('page', page);
              queryParams.append('limit', limit);

              if (category) {
                queryParams.append('category', category);
              }
              if (countryToExclude) {
                queryParams.append('countryToExclude', countryToExclude);
              }

              if (queryParams.toString()) {
                url += `?${queryParams.toString()}`;
              }

              const response = await apiClient.get(url);
              setSlice('articles', { news: response.data, isLoading: false });
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudieron cargar las noticias.';
              setSlice('articles', { isLoading: false, error: message, news: { docs: [], page: 1, totalPages: 1, totalDocs: 0 } });
            }
          },

          fetchSmartHomeFeed: async ({ page = 1, category = null, limit = 20, worldOnly = false, userLocation = null } = {}) => {
            setSlice('articles', { isLoading: true, error: null, news: { docs: [], page: 1, totalPages: 1, totalDocs: 0 } });
            try {
              const params = new URLSearchParams();
              params.append('page', page);
              params.append('limit', limit);

              if (category) {
                params.append('category', category);
              }

              if (worldOnly) {
                params.append('worldOnly', 'true');
              }

              if (userLocation) {
                params.append('userLocation', userLocation);
              }

              const response = await apiClient.get(`/posts/smart-home-feed?${params.toString()}`);
              setSlice('articles', { news: response.data, isLoading: false });
              return response.data;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo cargar el feed inteligente.';
              setSlice('articles', { isLoading: false, error: message, news: { docs: [], page: 1, totalPages: 1, totalDocs: 0 } });
              return null;
            }
          },

          uploadArticleImage: async (imageFile) => {
            const formData = new FormData();
            formData.append('media', imageFile);

            try {
              const response = await apiClient.post('/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              return response.data.url;
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo subir la imagen.');
              return null;
            }
          },

          fetchArticlesByAuthor: async (authorId, status = 'published', page = 1) => {
            setSlice('articles', { isLoading: true, error: null });
            try {
              const url = `/posts/author/${authorId}?status=${status}&page=${page}`;
              const token = localStorage.getItem('token');
              const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
              const response = await apiClient.get(url, config);
              setSlice('articles', { news: response.data, isLoading: false });
              return response.data;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudieron cargar los artículos.';
              setSlice('articles', { isLoading: false, error: message, news: { docs: [], page: 1, totalPages: 1, totalDocs: 0 } });
            }
          },

          fetchArticleNavigation: async (postId) => {
            const cachedNav = get().articles.navigationCache[postId];
            if (cachedNav) {
              return cachedNav;
            }

            try {
              const response = await apiClient.get(`/posts/${postId}/navigation`);
              const navigation = response.data;
              set((state) => ({
                articles: {
                  ...state.articles,
                  navigationCache: { ...state.articles.navigationCache, [postId]: navigation },
                },
              }));
              return navigation;
            } catch (err) {
              console.error('Error fetching article navigation:', err);
              return { prev: null, next: null };
            }
          },

          incrementPostView: async (postId) => {
            try {
              await apiClient.patch(`/posts/${postId}/view`);
            } catch (error) {
              console.error('Error al incrementar la vista del post:', error);
            }
          },

          // =========================
          // Feed
          // =========================
          fetchFeed: async (currentPage = 1) => {
            if (currentPage > 1 && !get().feed.hasMore) return;

            const authState = useAuthStore.getState();
            if (!authState.isLoggedIn || !authState.currentUser) {
              return;
            }

            setSlice('feed', { isLoading: true });
            try {
              const response = await apiClient.get(`/users/feed/posts?page=${currentPage}&limit=10`);
              const newPosts = response.data.posts;
              const hybridItems = Array.isArray(response.data?.hybridItems) ? response.data.hybridItems : [];

              const optimizedPosts = newPosts.map(optimizePost);

              set((state) => ({
                feed: {
                  ...state.feed,
                  posts: currentPage === 1 ? optimizedPosts : [...state.feed.posts, ...optimizedPosts],
                  hybridItems,
                  hasMore: newPosts.length === 10,
                  page: currentPage,
                  isLoading: false,
                },
              }));
            } catch (error) {
              toast.error('No se pudieron cargar las publicaciones.');
              setSlice('feed', { isLoading: false });
            }
          },

          loadMorePosts: () => {
            const nextPage = get().feed.page + 1;
            get().fetchFeed(nextPage);
          },

          fetchWallPosts: async (userId) => {
            if (get().feed.wallPosts[userId]) return;

            setSlice('feed', { isLoading: true });
            try {
              const response = await apiClient.get(`/posts/user/${userId}`);
              const optimizedPosts = response.data.map(optimizePost);
              set((state) => ({
                feed: {
                  ...state.feed,
                  wallPosts: { ...state.feed.wallPosts, [userId]: optimizedPosts },
                  isLoading: false,
                },
              }));
            } catch (error) {
              toast.error('No se pudieron cargar los posts del muro.');
              setSlice('feed', { isLoading: false });
            }
          },

          addPost: async (postData) => {
            const formData = new FormData();

            if (postData.type === 'creation') {
              formData.append('media', postData.image);
              formData.append('content', postData.content);
              formData.append('type', 'creation');
              formData.append('privacy', 'public');
              if (postData.creationStyle) {
                formData.append('creationStyle.textColor', postData.creationStyle.textColor);
                formData.append('creationStyle.fontSize', postData.creationStyle.fontSize);
                formData.append('creationStyle.fontFamily', postData.creationStyle.fontFamily);
              }
              if (postData.wallOwnerId) {
                formData.append('wallOwnerId', postData.wallOwnerId);
              }
            } else {
              formData.append('content', postData.content);
              formData.append('privacy', postData.privacy || 'public');
              postData.images?.forEach(image => formData.append('images', image.file || image));
              if (postData.repostedPostId) {
                formData.append('repostedPostId', postData.repostedPostId);
              }
              if (postData.wallOwnerId) {
                formData.append('wallOwnerId', postData.wallOwnerId);
              }
            }

            try {
              const response = await apiClient.post('/posts', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              const newPostFromServer = response.data;
              const optimizedPost = optimizePost(newPostFromServer);

              set((state) => {
                const wallOwnerId = newPostFromServer.wallOwner;
                const newWallPosts = { ...state.feed.wallPosts };

                if (newWallPosts[wallOwnerId]) {
                  newWallPosts[wallOwnerId] = [optimizedPost, ...newWallPosts[wallOwnerId]];
                }

                return {
                  feed: {
                    ...state.feed,
                    posts: [optimizedPost, ...state.feed.posts],
                    wallPosts: newWallPosts,
                  },
                };
              });

              toast.success('¡Publicación creada!');
              return { success: true, post: newPostFromServer };
            } catch (error) {
              toast.error('No se pudo crear la publicación.');
              return { success: false };
            }
          },

          forwardPost: async (postId, conversationIds) => {
            try {
              await apiClient.post('/posts/forward', { postId, conversationIds });
              toast.success('Publicación reenviada con éxito.');
              return { success: true };
            } catch (error) {
              toast.error('No se pudo reenviar la publicación.');
              return { success: false };
            }
          },

          deletePost: async (postId) => {
            try {
              await apiClient.delete(`/posts/${postId}`);
              toast.success('¡Publicación eliminada!');
              set((state) => ({
                feed: {
                  ...state.feed,
                  posts: state.feed.posts.filter((p) => p._id !== postId),
                  wallPosts: Object.fromEntries(
                    Object.entries(state.feed.wallPosts).map(([userId, posts]) => [
                      userId,
                      posts.filter((p) => p._id !== postId),
                    ])
                  ),
                },
              }));
              return { success: true };
            } catch (error) {
              toast.error('No se pudo eliminar la publicación.');
              return { success: false };
            }
          },

          updatePost: async (postId, postData) => {
            const formData = new FormData();

            if (postData.content !== undefined) {
              formData.append('content', postData.content);
            }
            if (postData.privacy !== undefined) {
              formData.append('privacy', postData.privacy);
            } else if (postData.content === undefined) {
              const currentPost = get().feed.posts.find(p => p._id === postId) ||
                Object.values(get().feed.wallPosts).flat().find(p => p._id === postId);
              if (currentPost && currentPost.privacy) {
                formData.append('privacy', currentPost.privacy);
              }
            }

            if (postData.images) {
              postData.images.forEach(image => formData.append('images', image));
            }

            try {
              const response = await apiClient.patch(`/posts/${postId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              const updatedPostFromServer = response.data;
              const optimizedPost = optimizePost(updatedPostFromServer);

              set((state) => ({
                feed: {
                  ...state.feed,
                  posts: state.feed.posts.map((p) => (p._id === optimizedPost._id ? optimizedPost : p)),
                  wallPosts: {
                    ...state.feed.wallPosts,
                    [updatedPostFromServer.wallOwner]: state.feed.wallPosts[updatedPostFromServer.wallOwner]?.map((p) =>
                      p._id === optimizedPost._id ? optimizedPost : p
                    ),
                  },
                },
              }));

              toast.success('¡Publicación actualizada!');
              return { success: true, post: updatedPostFromServer };
            } catch (error) {
              toast.error('No se pudo actualizar la publicación.');
              return { success: false };
            }
          },

          updatePostPrivacy: async (postId, newPrivacy) => {
            try {
              const result = await get().updatePost(postId, { privacy: newPrivacy });
              if (result.success) {
                toast.success('Privacidad de la publicación actualizada.');
              }
              return result;
            } catch (error) {
              toast.error('No se pudo actualizar la privacidad de la publicación.');
              return { success: false };
            }
          },

          likePost: async (postId, reactionType = 'like') => {
            try {
              const response = await apiClient.post(`/posts/${postId}/like`, { reactionType });
              const updatedPost = optimizePost(response.data);

              set((state) => ({
                feed: {
                  ...state.feed,
                  posts: state.feed.posts.map((p) => (p._id === postId ? updatedPost : p)),
                  wallPosts: Object.fromEntries(
                    Object.entries(state.feed.wallPosts).map(([userId, posts]) => [
                      userId,
                      posts.map((p) => (p._id === postId ? updatedPost : p)),
                    ])
                  ),
                },
              }));

              return { success: true };
            } catch (error) {
              toast.error('No se pudo procesar la reacción.');
              return { success: false };
            }
          },

          addComment: async (postId, commentText) => {
            try {
              const response = await apiClient.post(`/posts/${postId}/comments`, {
                text: commentText,
                content: commentText,
              });
              const updatedPost = response.data ? optimizePost(response.data) : null;
              if (!updatedPost?._id) {
                const authState = useAuthStore.getState();
                const optimisticComment = {
                  _id: `local-${Date.now()}`,
                  text: commentText,
                  createdAt: new Date().toISOString(),
                  author: authState?.currentUser || null,
                  likes: [],
                  replies: [],
                };
                set((state) => ({
                  feed: {
                    ...state.feed,
                    posts: state.feed.posts.map((p) => (
                      p._id === postId
                        ? { ...p, comments: [optimisticComment, ...(p.comments || [])] }
                        : p
                    )),
                    wallPosts: Object.fromEntries(
                      Object.entries(state.feed.wallPosts).map(([userId, posts]) => [
                        userId,
                        posts.map((p) => (
                          p._id === postId
                            ? { ...p, comments: [optimisticComment, ...(p.comments || [])] }
                            : p
                        )),
                      ])
                    ),
                  },
                }));
                toast.success('Comentario añadido');
                return { success: true };
              }

              set((state) => ({
                feed: {
                  ...state.feed,
                  posts: state.feed.posts.map((p) => (p._id === postId ? updatedPost : p)),
                  wallPosts: Object.fromEntries(
                    Object.entries(state.feed.wallPosts).map(([userId, posts]) => [
                      userId,
                      posts.map((p) => (p._id === postId ? updatedPost : p)),
                    ])
                  ),
                },
              }));

              toast.success('Comentario añadido');
              return { success: true };
            } catch (error) {
              toast.error('No se pudo añadir el comentario.');
              return { success: false };
            }
          },

          likeComment: async (postId, commentId, reactionType = 'like') => {
            try {
              const response = await apiClient.post(
                `/posts/${postId}/comments/${commentId}/like`,
                { reactionType }
              );
              const updatedPost = optimizePost(response.data);

              set((state) => ({
                feed: {
                  ...state.feed,
                  posts: state.feed.posts.map((p) => (p._id === postId ? updatedPost : p)),
                  wallPosts: Object.fromEntries(
                    Object.entries(state.feed.wallPosts).map(([userId, posts]) => [
                      userId,
                      posts.map((p) => (p._id === postId ? updatedPost : p)),
                    ])
                  ),
                },
              }));

              return { success: true };
            } catch (error) {
              toast.error('No se pudo procesar la reacción del comentario.');
              return { success: false };
            }
          },

          addReplyToComment: async (postId, commentId, text) => {
            try {
              const response = await apiClient.post(
                `/posts/${postId}/comments/${commentId}/reply`,
                { text }
              );
              const updatedPost = optimizePost(response.data);

              set((state) => ({
                feed: {
                  ...state.feed,
                  posts: state.feed.posts.map((p) => (p._id === postId ? updatedPost : p)),
                  wallPosts: Object.fromEntries(
                    Object.entries(state.feed.wallPosts).map(([userId, posts]) => [
                      userId,
                      posts.map((p) => (p._id === postId ? updatedPost : p)),
                    ])
                  ),
                },
              }));

              return { success: true };
            } catch (error) {
              toast.error('No se pudo responder el comentario.');
              return { success: false };
            }
          },

          setAuthUser: (user) => {
            if (user) {
              set((state) => ({
                feed: {
                  ...state.feed,
                  authUserId: user._id,
                  authUsername: user.username,
                  authUserAvatar: user.avatar,
                },
              }));
            }
          },

          // =========================
          // Stories
          // =========================
          fetchStories: async () => {
            setSlice('stories', { isLoading: true });
            try {
              const response = await apiClient.get('/stories');
              if (Array.isArray(response.data)) {
                // Buscar historia soberana
                const sovereignStory = response.data.find(story => story.zone === 'historias' && story.isSovereign);
                let optimizedStories = response.data.map(optimizeStory);
                if (sovereignStory) {
                  // Inyectar en posición [0]
                  optimizedStories = [optimizeStory(sovereignStory), ...optimizedStories.filter(s => s._id !== sovereignStory._id)];
                }
                setSlice('stories', { stories: optimizedStories, isLoading: false });
              }
            } catch (error) {
              toast.error('No se pudieron cargar las historias.');
              setSlice('stories', { isLoading: false });
            }
          },

          addStory: async (storyData) => {
            let formData;

            if (storyData instanceof FormData) {
              formData = storyData;
            } else {
              formData = new FormData();
              const sceneData = {
                type: storyData.type,
                text: storyData.text,
                background: storyData.background,
                textElements: storyData.textElements || [],
                emojiElements: storyData.emojiElements || [],
              };

              if (storyData.media) {
                formData.append('scenes', storyData.media);
              }

              formData.append('scenesMetadata', JSON.stringify([sceneData]));

              if (storyData.privacy) {
                formData.append('privacy', storyData.privacy);
              }
            }

            try {
              const response = await apiClient.post('/stories', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              const newStory = optimizeStory(response.data);
              set((state) => {
                const existingStoryIndex = state.stories.stories.findIndex(s => s._id === newStory._id);
                if (existingStoryIndex !== -1) {
                  const updatedStories = [...state.stories.stories];
                  updatedStories[existingStoryIndex] = newStory;
                  return { stories: { ...state.stories, stories: updatedStories } };
                }
                return { stories: { ...state.stories, stories: [newStory, ...state.stories.stories] } };
              });
              toast.success('¡Historia creada!');
              return { success: true, story: newStory };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo crear la historia.');
              return { success: false };
            }
          },

          fetchArchivedStories: async () => {
            setSlice('stories', { isLoadingArchived: true });
            try {
              const response = await apiClient.get('/stories/archived');
              setSlice('stories', { archivedStories: response.data, isLoadingArchived: false });
            } catch (error) {
              toast.error('No se pudieron cargar las historias archivadas.');
              setSlice('stories', { isLoadingArchived: false });
            }
          },

          archiveStory: async (storyId) => {
            const originalStories = get().stories.stories;
            const storyToArchive = originalStories.find((s) => s._id === storyId);
            if (!storyToArchive) return;

            set((state) => ({
              stories: {
                ...state.stories,
                stories: state.stories.stories.filter((s) => s._id !== storyId),
                archivedStories: [storyToArchive, ...state.stories.archivedStories],
              },
            }));

            try {
              await apiClient.post(`/stories/${storyId}/archive`);
              toast.success('Historia archivada.');
            } catch (error) {
              toast.error('No se pudo archivar la historia.');
              setSlice('stories', { stories: originalStories });
            }
          },

          likeStory: async (storyId) => {
            const originalStories = get().stories.stories;
            set((state) => ({
              stories: {
                ...state.stories,
                stories: state.stories.stories.map((s) => {
                  if (s._id === storyId) {
                    const currentUserId = useAuthStore.getState().currentUser?._id;
                    const isLiked = s.likes.includes(currentUserId);
                    const newLikes = isLiked
                      ? s.likes.filter((id) => id !== currentUserId)
                      : [...s.likes, currentUserId];
                    return { ...s, likes: newLikes };
                  }
                  return s;
                }),
              },
            }));

            try {
              await apiClient.post(`/stories/${storyId}/like`);
            } catch (error) {
              setSlice('stories', { stories: originalStories });
            }
          },

          deleteStory: async (storyId) => {
            const originalStories = get().stories.stories;
            const originalArchivedStories = get().stories.archivedStories;

            set((state) => ({
              stories: {
                ...state.stories,
                stories: state.stories.stories.filter((s) => s._id !== storyId),
                archivedStories: state.stories.archivedStories.filter((s) => s._id !== storyId),
              },
            }));

            try {
              await apiClient.delete(`/stories/${storyId}`);
              toast.success('Historia eliminada.');
              return { success: true };
            } catch (error) {
              toast.error('No se pudo eliminar la historia.');
              set((state) => ({
                stories: { ...state.stories, stories: originalStories, archivedStories: originalArchivedStories },
              }));
              return { success: false };
            }
          },

          // =========================
          // Jobs
          // =========================
          fetchJobs: async (filters = {}) => {
            setSlice('jobs', { isLoading: true, error: null });
            try {
              const response = await apiClient.get('/jobs', { params: filters });
              setSlice('jobs', { jobs: response.data, isLoading: false });
            } catch (err) {
              const message = err.response?.data?.message || 'Error al cargar las ofertas de trabajo.';
              setSlice('jobs', { error: message, isLoading: false });
              toast.error(message);
            }
          },

          fetchJobById: async (jobId) => {
            setSlice('jobs', { isLoading: true, error: null, currentJob: null });
            try {
              const jobsList = get().jobs.jobs;
              let job = jobsList.find(j => j._id === jobId);
              if (!job) {
                const response = await apiClient.get(`/jobs/${jobId}`);
                job = response.data;
              }
              setSlice('jobs', { currentJob: job, isLoading: false });
            } catch (err) {
              const message = err.response?.data?.message || 'Error al cargar los detalles de la oferta.';
              setSlice('jobs', { error: message, isLoading: false });
              toast.error(message);
            }
          },

          applyToJob: async (jobId) => {
            if (get().jobs.isApplying) return;

            setSlice('jobs', { isApplying: true });
            try {
              const response = await apiClient.post(`/jobs/${jobId}/apply`);
              toast.success(response.data.message);

              set((state) => ({
                jobs: {
                  ...state.jobs,
                  currentJob: state.jobs.currentJob ? { ...state.jobs.currentJob, hasApplied: true } : null,
                  isApplying: false,
                },
              }));

              return { success: true };
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo procesar tu aplicación.';
              toast.error(message);

              if (err.response?.status === 409) {
                set((state) => ({
                  jobs: {
                    ...state.jobs,
                    currentJob: state.jobs.currentJob ? { ...state.jobs.currentJob, hasApplied: true } : null,
                  },
                }));
              }

              setSlice('jobs', { isApplying: false });
              return { success: false, message };
            }
          },

          saveJobAlert: async (filters) => {
            try {
              const response = await apiClient.post('/job-alerts', { filters });
              toast.success(response.data.message);
              return { success: true };
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo guardar la alerta.';
              toast.error(message);
              return { success: false, message };
            }
          },

          createJob: async (jobData) => {
            setSlice('jobs', { isLoading: true });
            try {
              const response = await apiClient.post('/jobs', jobData);
              toast.success(response.data.message);
              set((state) => ({
                jobs: {
                  ...state.jobs,
                  jobs: [response.data.job, ...state.jobs.jobs],
                  isLoading: false,
                },
              }));
              return { success: true, job: response.data.job };
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo crear la oferta de trabajo.';
              setSlice('jobs', { error: message, isLoading: false });
              toast.error(message);
              return { success: false, message };
            }
          },

          // =========================
          // Polls
          // =========================
          fetchPolls: async () => {
            setSlice('polls', { isLoading: true, error: null });
            try {
              const response = await apiClient.get('/polls');
              setSlice('polls', { polls: response.data || [], isLoading: false });
              return response.data;
            } catch (error) {
              const errorMsg = error.response?.data?.message || 'Error cargando encuestas';
              setSlice('polls', { error: errorMsg, isLoading: false });
              toast.error(errorMsg);
              return [];
            }
          },

          fetchPoll: async (pollId) => {
            setSlice('polls', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/polls/${pollId}`);
              const poll = response.data;
              set((state) => ({
                polls: {
                  ...state.polls,
                  polls: state.polls.polls.map((p) => p._id === pollId ? poll : p),
                  selectedPoll: poll,
                  isLoading: false,
                },
              }));
              return poll;
            } catch (error) {
              const errorMsg = error.response?.data?.message || 'Error cargando encuesta';
              setSlice('polls', { error: errorMsg, isLoading: false });
              toast.error(errorMsg);
              return null;
            }
          },

          voteOnPoll: async (pollId, optionId) => {
            if (!pollId || !optionId) {
              toast.error('Datos de voto inválidos');
              return { success: false };
            }

            const previousPolls = get().polls.polls;
            set((state) => ({
              polls: {
                ...state.polls,
                userVotes: { ...state.polls.userVotes, [pollId]: optionId },
                polls: state.polls.polls.map((poll) => {
                  if (poll._id !== pollId) return poll;
                  return {
                    ...poll,
                    options: poll.options.map((opt) => ({
                      ...opt,
                      votes: opt._id === optionId ? (opt.votes || 0) + 1 : opt.votes || 0,
                    })),
                  };
                }),
              },
            }));

            try {
              const response = await apiClient.post(`/polls/${pollId}/vote`, {
                selectedOptionId: optionId,
              });

              const updatedPoll = response.data;
              set((state) => ({
                polls: {
                  ...state.polls,
                  polls: state.polls.polls.map((p) => p._id === pollId ? updatedPoll : p),
                  selectedPoll: state.polls.selectedPoll?._id === pollId ? updatedPoll : state.polls.selectedPoll,
                  userVotes: { ...state.polls.userVotes, [pollId]: optionId },
                },
              }));

              return { success: true, poll: updatedPoll };
            } catch (error) {
              setSlice('polls', { polls: previousPolls });
              const errorMsg = error.response?.data?.message || 'No se pudo registrar tu voto';
              setSlice('polls', { error: errorMsg });
              toast.error(errorMsg);
              return { success: false, error: errorMsg };
            }
          },

          createPoll: async (pollData) => {
            setSlice('polls', { isLoading: true, error: null });
            try {
              const response = await apiClient.post('/polls', pollData);
              const newPoll = response.data;

              set((state) => ({
                polls: {
                  ...state.polls,
                  polls: [newPoll, ...state.polls.polls],
                  isLoading: false,
                },
              }));

              toast.success('Encuesta creada exitosamente');
              return { success: true, poll: newPoll };
            } catch (error) {
              const errorMsg = error.response?.data?.message || 'Error creando encuesta';
              setSlice('polls', { error: errorMsg, isLoading: false });
              toast.error(errorMsg);
              return { success: false, error: errorMsg };
            }
          },

          closePoll: async (pollId) => {
            try {
              const response = await apiClient.patch(`/polls/${pollId}/close`);
              const updatedPoll = response.data;

              set((state) => ({
                polls: {
                  ...state.polls,
                  polls: state.polls.polls.map((p) => p._id === pollId ? updatedPoll : p),
                  selectedPoll: state.polls.selectedPoll?._id === pollId ? updatedPoll : state.polls.selectedPoll,
                },
              }));

              toast.success('Encuesta cerrada');
              return { success: true, poll: updatedPoll };
            } catch (error) {
              const errorMsg = error.response?.data?.message || 'Error cerrando encuesta';
              toast.error(errorMsg);
              return { success: false, error: errorMsg };
            }
          },

          setSelectedPoll: (poll) => {
            setSlice('polls', { selectedPoll: poll });
          },

          resetPolls: () => {
            setSlice('polls', {
              polls: [],
              selectedPoll: null,
              isLoading: false,
              error: null,
              userVotes: {},
            });
          },

          subscribeToPolls: () => {
            const socket = getSocket();
            if (!socket) return;

            socket.on('poll_updated', (updatedPoll) => {
              set((state) => ({
                polls: {
                  ...state.polls,
                  polls: state.polls.polls.map((p) => p._id === updatedPoll._id ? updatedPoll : p),
                  selectedPoll: state.polls.selectedPoll?._id === updatedPoll._id ? updatedPoll : state.polls.selectedPoll,
                },
              }));
            });

            socket.on('poll_closed', (closedPollId) => {
              set((state) => ({
                polls: {
                  ...state.polls,
                  polls: state.polls.polls.map((p) =>
                    p._id === closedPollId ? { ...p, isActive: false } : p
                  ),
                },
              }));
            });
          },

          // =========================
          // Surveys
          // =========================
          fetchSurvey: async (surveyId) => {
            const cachedSurvey = get().surveys.surveyCache[surveyId];
            if (cachedSurvey) {
              return cachedSurvey;
            }

            setSlice('surveys', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/surveys/${surveyId}`);
              const survey = response.data;
              set((state) => ({
                surveys: {
                  ...state.surveys,
                  surveyCache: { ...state.surveys.surveyCache, [surveyId]: survey },
                  isLoading: false,
                },
              }));
              return survey;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo cargar la encuesta.';
              setSlice('surveys', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          submitSurveyResponse: async (surveyId, responseData) => {
            setSlice('surveys', { isSubmitting: true, error: null });
            try {
              const response = await apiClient.post(`/surveys/${surveyId}/responses`, responseData);
              toast.success(response.data.message);
              setSlice('surveys', { isSubmitting: false });
              return { success: true };
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo enviar la encuesta.';
              setSlice('surveys', { isSubmitting: false, error: message });
              toast.error(message);
              return { success: false, error: message };
            }
          },

          // =========================
          // Public Ads
          // =========================
          fetchPublicAds: async (params) => {
            setSlice('publicAds', { isLoading: true, error: null });
            try {
              const response = await apiClient.get('/ads/public', { params });
              const ads = response.data?.data?.ads || [];
              setSlice('publicAds', { ads, isLoading: false });
              return ads;
            } catch (error) {
              const errorMessage = error.response?.data?.message || 'No se pudieron cargar los anuncios públicos.';
              toast.error(errorMessage);
              setSlice('publicAds', { isLoading: false, error: errorMessage });
              return null;
            }
          },

          fetchDefaultAssets: async (params = {}) => {
            const hasCustomParams = Boolean(params && Object.keys(params).length > 0);
            if (get().defaultAssets.assets && !get().defaultAssets.isLoading && !hasCustomParams) {
              return get().defaultAssets.assets;
            }

            setSlice('defaultAssets', { isLoading: true, error: null });
            try {
              let sessionKey = '';
              try {
                const storageKey = 'agora_content_session_key';
                sessionKey = window.localStorage.getItem(storageKey) || '';
                if (!sessionKey) {
                  sessionKey = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                  window.localStorage.setItem(storageKey, sessionKey);
                }
              } catch {
                sessionKey = '';
              }

              const response = await apiClient.get('/admin/default-assets', {
                params: {
                  ...(params || {}),
                  sessionKey,
                },
              });
              setSlice('defaultAssets', { assets: response.data, isLoading: false, error: null });
              return response.data;
            } catch (error) {
              const message = error.response?.data?.message || 'No se pudieron cargar los assets por defecto.';
              setSlice('defaultAssets', { isLoading: false, error: message });
              return null;
            }
          },

          clearAds: () => setSlice('publicAds', { ads: [], isLoading: false, error: null }),

          // =========================
          // User Ads
          // =========================
          fetchMyAds: async () => {
            setSlice('userAds', { isLoading: true, error: null });
            try {
              const response = await apiClient.get('/ads/my-ads');
              const ads = response.data?.data?.ads || [];
              setSlice('userAds', { myAds: ads, isLoading: false });
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudieron cargar tus anuncios.';
              setSlice('userAds', { isLoading: false, error: message });
              toast.error(message);
            }
          },

          createAd: async (adData) => {
            setSlice('userAds', { isLoading: true });
            try {
              const response = await apiClient.post('/ads/create', adData);
              const createdAd = response.data?.data?.ad;
              set((state) => ({
                userAds: {
                  ...state.userAds,
                  myAds: createdAd ? [createdAd, ...state.userAds.myAds] : state.userAds.myAds,
                  isLoading: false,
                },
              }));
              toast.success('Anuncio creado con éxito. Está pendiente de revisión.');
              return createdAd;
            } catch (error) {
              setSlice('userAds', { isLoading: false });
              toast.error(error.response?.data?.message || 'No se pudo crear el anuncio.');
              throw error;
            }
          },

          // =========================
          // Categories
          // =========================
          fetchCategories: async () => {
            if (get().categories.categories.length > 0 && !get().categories.isLoading) {
              return;
            }
            setSlice('categories', { isLoading: true });
            try {
              const response = await apiClient.get('/categories');
              setSlice('categories', { categories: response.data, isLoading: false });
            } catch (error) {
              const message = error.response?.data?.message || 'No se pudieron cargar las categorías.';
              setSlice('categories', { error: message, isLoading: false });
            }
          },

          // =========================
          // Stores
          // =========================
          fetchStoreByName: async (name) => {
            const nameLowerCase = name.toLowerCase();
            // 🟢 FIX #3: Cache hit - retornar datos sin re-render
            if (get().stores.storesCache[nameLowerCase]) {
              return get().stores.storesCache[nameLowerCase];
            }

            setSlice('stores', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/stores/name/${name}`);
              const storeData = response.data;
              
              // 🟢 FIX #3: Batch update (single state change)
              set((state) => ({
                stores: {
                  ...state.stores,
                  storesCache: {
                    ...state.stores.storesCache,
                    [nameLowerCase]: storeData,
                  },
                  isLoading: false,
                  error: null,
                },
              }));
              
              return storeData;
            } catch (error) {
              const errorMessage = error.response?.data?.message || 'No se pudo cargar la tienda.';
              // 🟢 FIX #3: No usar toast aquí (evita memory leak)
              console.error('❌ fetchStoreByName error:', errorMessage);
              setSlice('stores', { error: errorMessage, isLoading: false });
              throw error;
            }
          },

          fetchStores: async () => {
            setSlice('stores', { isLoading: true });
            try {
              const response = await apiClient.get('/stores');
              setSlice('stores', { stores: response.data, isLoading: false });
            } catch (error) {
              toast.error('No se pudieron cargar las tiendas.');
              setSlice('stores', { isLoading: false });
            }
          },

          // =========================
          // Bloggers
          // =========================
          fetchBloggers: async () => {
            if (get().bloggers.bloggers.length > 0 && !get().bloggers.isLoading) {
              return;
            }
            setSlice('bloggers', { isLoading: true, error: null });
            try {
              const response = await apiClient.get('/users/public/role/blogger');
              setSlice('bloggers', { bloggers: response.data, isLoading: false });
            } catch (err) {
              // 🔴 MANEJO SILENCIOSO: 404 es normal si no hay bloggers
              if (err.response?.status === 404 || err.response?.status === 401) {
                setSlice('bloggers', { bloggers: [], isLoading: false, error: null });
                return;
              }
              const message = err.response?.data?.message || 'No se pudieron cargar los creadores.';
              setSlice('bloggers', { isLoading: false, error: message });
            }
          },

          // =========================
          // Campaigns
          // =========================
          fetchCampaignsByPage: async (pageId) => {
            setSlice('campaigns', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/campaigns/page/${pageId}`);
              setSlice('campaigns', { campaigns: response.data, isLoading: false });
              return response.data;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudieron cargar las campañas.';
              setSlice('campaigns', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          createCampaign: async (campaignData) => {
            setSlice('campaigns', { isLoading: true });
            try {
              const response = await apiClient.post('/campaigns', campaignData);
              const newCampaign = response.data;
              set((state) => ({
                campaigns: {
                  ...state.campaigns,
                  campaigns: [newCampaign, ...state.campaigns.campaigns],
                  isLoading: false,
                },
              }));
              toast.success('¡Campaña creada con éxito!');
              return newCampaign;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo crear la campaña.';
              setSlice('campaigns', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          fetchCampaignDetails: async (campaignId) => {
            setSlice('campaigns', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/campaigns/${campaignId}`);
              setSlice('campaigns', { currentCampaignDetails: response.data, isLoading: false });
              return response.data;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudieron cargar los detalles de la campaña.';
              setSlice('campaigns', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          // =========================
          // Company
          // =========================
          fetchCompanyByName: async (companyName) => {
            if (get().company.isLoading) {
              return;
            }

            const lowerCaseName = companyName.toLowerCase();
            const cachedData = get().company.companyCache[lowerCaseName];
            if (cachedData) {
              setSlice('company', { company: cachedData.company });
              return cachedData;
            }

            setSlice('company', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/companies/${companyName}`);
              const companyData = response.data;
              set((state) => ({
                company: {
                  ...state.company,
                  company: companyData.company,
                  companyCache: { ...state.company.companyCache, [lowerCaseName]: companyData },
                  isLoading: false,
                },
              }));
              return companyData;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo cargar la página de la empresa.';
              setSlice('company', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          setCompanyField: (field, value) => {
            set((state) => ({
              company: {
                ...state.company,
                company: { ...state.company.company, [field]: value },
              },
            }));
          },

          fetchCompanyById: async (companyId) => {
            const cachedData = Object.values(get().company.companyCache).find(
              (data) => data.company?._id === companyId
            );
            if (cachedData) {
              setSlice('company', { company: cachedData.company });
              return cachedData;
            }

            setSlice('company', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/companies/id/${companyId}`);
              const company = response.data;
              set((state) => ({
                company: {
                  ...state.company,
                  company,
                  companyCache: {
                    ...state.company.companyCache,
                    [companyId]: { company, jobs: [], ads: [] },
                    [company.name.toLowerCase()]: { company, jobs: [], ads: [] },
                  },
                  isLoading: false,
                },
              }));
              return company;
            } catch (err) {
              const error = {
                message: err.response?.data?.message || 'No se pudo cargar la empresa por ID.',
                status: err.response?.status,
              };
              setSlice('company', { isLoading: false, error });
              toast.error(error.message);
              return null;
            }
          },

          updateCompany: async () => {
            const { company } = get().company;
            if (!company) return null;

            setSlice('company', { isSubmitting: true });

            const formData = new FormData();
            formData.append('name', company.name);
            formData.append('description', company.description || '');
            formData.append('website', company.website || '');
            formData.append('industry', company.industry || '');
            if (company.logoFile) formData.append('logo', company.logoFile);
            if (company.coverPhotoFile) formData.append('coverPhoto', company.coverPhotoFile);

            try {
              const response = await apiClient.patch(`/companies/${company._id}`, formData);
              const updatedCompany = response.data;

              setSlice('company', { company: updatedCompany, isSubmitting: false });
              toast.success('¡Página de empresa actualizada!');
              return updatedCompany;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo actualizar la empresa.';
              setSlice('company', { isSubmitting: false, error: message });
              toast.error(message);
              return null;
            }
          },

          activateCompany: async (companyId) => {
            setSlice('company', { isLoading: true });
            try {
              const response = await apiClient.patch(`/companies/${companyId}/activate`);
              const updatedCompany = response.data;
              set((state) => ({
                company: {
                  ...state.company,
                  companyCache: {
                    ...state.company.companyCache,
                    [updatedCompany.name.toLowerCase()]: {
                      ...state.company.companyCache[updatedCompany.name.toLowerCase()],
                      company: updatedCompany,
                    },
                  },
                  isLoading: false,
                },
              }));
              toast.success('Empresa activada con éxito.');
              return updatedCompany;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo activar la empresa.';
              setSlice('company', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          deactivateCompany: async (companyId) => {
            setSlice('company', { isLoading: true });
            try {
              const response = await apiClient.patch(`/companies/${companyId}/deactivate`);
              const updatedCompany = response.data;
              set((state) => ({
                company: {
                  ...state.company,
                  companyCache: {
                    ...state.company.companyCache,
                    [updatedCompany.name.toLowerCase()]: {
                      ...state.company.companyCache[updatedCompany.name.toLowerCase()],
                      company: updatedCompany,
                    },
                  },
                  isLoading: false,
                },
              }));
              toast.success('Empresa desactivada con éxito.');
              return updatedCompany;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo desactivar la empresa.';
              setSlice('company', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          deleteCompany: async (companyId) => {
            setSlice('company', { isLoading: true });
            try {
              await apiClient.delete(`/companies/${companyId}`);
              set((state) => {
                const newCache = { ...state.company.companyCache };
                for (const key in newCache) {
                  if (newCache[key].company?._id === companyId) {
                    delete newCache[key];
                    break;
                  }
                }
                return {
                  company: {
                    ...state.company,
                    companyCache: newCache,
                    isLoading: false,
                  },
                };
              });
              toast.success('Solicitud de eliminación de empresa enviada. Se eliminará en 72 horas.');
              return true;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo solicitar la eliminación de la empresa.';
              setSlice('company', { isLoading: false, error: message });
              toast.error(message);
              return false;
            }
          },

          cancelCompanyDeletion: async (companyId) => {
            setSlice('company', { isLoading: true });
            try {
              const response = await apiClient.patch(`/companies/${companyId}/cancel-deletion`);
              const updatedCompany = response.data;
              set((state) => ({
                company: {
                  ...state.company,
                  companyCache: {
                    ...state.company.companyCache,
                    [updatedCompany.name.toLowerCase()]: {
                      ...state.company.companyCache[updatedCompany.name.toLowerCase()],
                      company: updatedCompany,
                    },
                  },
                  isLoading: false,
                },
              }));
              toast.success('Eliminación de empresa cancelada.');
              return updatedCompany;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo cancelar la eliminación de la empresa.';
              setSlice('company', { isLoading: false, error: message });
              toast.error(message);
              return null;
            }
          },

          createJobForCompany: async (companyId, jobData) => {
            setSlice('company', { isSubmitting: true });
            try {
              const response = await apiClient.post('/jobs', { ...jobData, company: companyId });
              const newJob = response.data.job;

              set((state) => {
                const companyName = Object.keys(state.company.companyCache).find(
                  (key) => state.company.companyCache[key].company._id === companyId
                );
                if (companyName) {
                  return {
                    company: {
                      ...state.company,
                      companyCache: {
                        ...state.company.companyCache,
                        [companyName]: {
                          ...state.company.companyCache[companyName],
                          jobs: [newJob, ...state.company.companyCache[companyName].jobs],
                        },
                      },
                      isSubmitting: false,
                    },
                  };
                }
                return { company: { ...state.company, isSubmitting: false } };
              });

              toast.success('Vacante creada correctamente.');
              return newJob;
            } catch (err) {
              const message = err.response?.data?.message || 'No se pudo crear la vacante.';
              setSlice('company', { isSubmitting: false, error: message });
              toast.error(message);
              return null;
            }
          },

          // =========================
          // Albums
          // =========================
          fetchAlbumCollections: async (username) => {
            try {
              setSlice('albums', { isLoading: true });
              const response = await apiClient.get(`/albums/${username}/collections`);
              const collections = response.data?.collections || [];
              setSlice('albums', { collections, isLoading: false });
              return collections;
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudieron cargar las colecciones.');
              setSlice('albums', { isLoading: false });
              return null;
            }
          },

          fetchAlbumPhotos: async (username, albumType = 'profile', albumId = null) => {
            try {
              const response = await apiClient.get(`/albums/${username}`, {
                params: { albumType, albumId },
              });
              return response.data;
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudieron cargar las fotos del álbum.');
              return null;
            }
          },

          addPhotosToAlbum: async (payload) => {
            setSlice('albums', { isUploading: true, uploadProgress: 0 });

            try {
              const response = await apiClient.post('/albums/add', payload);
              toast.success(response.data.message || '¡Fotos añadidas con éxito!');
              setSlice('albums', { isUploading: false });
              return { success: true };
            } catch (error) {
              const errorMessage = error.response?.data?.message || 'No se pudieron añadir las fotos.';
              toast.error(errorMessage);
              setSlice('albums', { isUploading: false });
              return { success: false, error: errorMessage };
            }
          },

          updatePhotoVisibility: async (photoId, isPublic) => {
            try {
              const response = await apiClient.patch(`/albums/photos/${photoId}/visibility`, { isPublic });
              toast.success(response.data.message || 'Visibilidad actualizada.');
              return { success: true, album: response.data.album };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo actualizar la visibilidad.');
              return { success: false };
            }
          },

          updateAlbumVisibility: async (albumId, isPublic) => {
            try {
              const response = await apiClient.patch('/albums/visibility', { albumId, isPublic });
              toast.success(response.data.message || 'Visibilidad del album actualizada.');
              return { success: true, album: response.data.album };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo actualizar la visibilidad del album.');
              return { success: false };
            }
          },

          // =========================
          // Clips
          // =========================
          uploadClip: async (clipBlob, content, privacy) => {
            setSlice('clips', { isUploading: true, uploadProgress: 0 });

            const formData = new FormData();
            formData.append('clip', clipBlob, `clip-${Date.now()}.webm`);
            formData.append('content', content);
            formData.append('privacy', privacy);

            try {
              const response = await apiClient.post('/clips/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                  const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                  setSlice('clips', { uploadProgress: percentCompleted });
                },
              });

              const newClip = response.data;
              await get().addPost(newClip);

              const clipUrl = newClip?.videos?.[0] || newClip?.url || newClip?.mediaUrl || '';
              if (clipUrl) {
                const nextClip = {
                  url: clipUrl,
                  publicId: clipUrl.split('/').pop()?.split('.')?.[0] || null,
                  title: content || 'Sin título',
                  createdAt: newClip.createdAt || new Date().toISOString(),
                };

                const authState = useAuthStore.getState();
                const currentUser = authState.currentUser;
                if (currentUser) {
                  const existingClips = Array.isArray(currentUser.clips) ? currentUser.clips : [];
                  const hasClip = existingClips.some((clip) => clip?.url === nextClip.url);
                  const updatedClips = hasClip ? existingClips : [...existingClips, nextClip];
                  authState.setCurrentUser({ ...currentUser, clips: updatedClips });
                  if (currentUser.username) {
                    get().updateProfileDataInCache(currentUser.username, {
                      user: { ...currentUser, clips: updatedClips },
                    });
                  }
                }
              }

              toast.success('¡Clip subido con éxito!');
              setSlice('clips', { isUploading: false });
              return { success: true, clip: newClip };
            } catch (error) {
              toast.error('No se pudo subir el clip.');
              setSlice('clips', { isUploading: false });
              return { success: false };
            }
          },

          // =========================
          // Users
          // =========================
          fetchUsers: async () => {
            setSlice('users', { isLoading: true, error: null });
            try {
              const response = await apiClient.get('/users/users');
              setSlice('users', { users: response.data, isLoading: false });
            } catch (err) {
              const errorMessage = err.response?.data?.message || 'No se pudieron cargar los usuarios.';
              setSlice('users', { error: errorMessage, isLoading: false });
              toast.error(errorMessage);
            }
          },

          toggleFollowUser: async (userId) => {
            const currentUsers = get().users.users || [];
            const targetUser = currentUsers.find((user) => user?._id === userId);

            if (!targetUser) {
              return { success: false, error: 'Usuario no encontrado.' };
            }

            const isFollowing = !!targetUser.isFollowing;
            const endpoint = isFollowing ? `/users/unfollow/${userId}` : `/users/follow/${userId}`;
            const newFollowersCount = Math.max(0, (targetUser.followersCount || 0) + (isFollowing ? -1 : 1));

            set((state) => ({
              users: {
                ...state.users,
                users: currentUsers.map((user) =>
                  user?._id === userId
                    ? { ...user, isFollowing: !isFollowing, followersCount: newFollowersCount }
                    : user
                ),
              },
            }));

            try {
              await apiClient.post(endpoint);
              return { success: true };
            } catch (error) {
              setSlice('users', { users: currentUsers });
              toast.error(error.response?.data?.message || 'No se pudo completar la acción.');
              return { success: false, error: error.response?.data?.message };
            }
          },

          // =========================
          // Profiles
          // =========================
          getProfile: (username) => {
            if (get().profiles.isLoading) return;
            const cachedProfile = get().profiles.profilesCache[username.toLowerCase()];
            if (cachedProfile) {
              setSlice('profiles', { isLoading: false });
              return;
            }
            get().fetchProfile(username);
          },

          fetchProfile: async (username, params) => {
            setSlice('profiles', { isLoading: true, error: null });
            try {
              const response = await apiClient.get(`/users/profile/${username}`, { params });
              const profileData = response.data;

              if (profileData.user) {
                profileData.user.avatar = normalizeMediaObject(profileData.user.avatar);
                profileData.user.coverPhoto = normalizeMediaObject(profileData.user.coverPhoto);
                profileData.user.profileType = profileData.user.profileType || 'normal';
                profileData.user.stats = {
                  totalLikes: profileData.user.stats?.totalLikes ?? 0,
                  totalGifts: profileData.user.stats?.totalGifts ?? 0,
                  ...profileData.user.stats,
                };
              }

              set((state) => ({
                profiles: {
                  ...state.profiles,
                  profilesCache: {
                    ...state.profiles.profilesCache,
                    [username.toLowerCase()]: profileData,
                  },
                  isLoading: false,
                },
              }));

              return profileData;
            } catch (error) {
              const errorMessage = error.response?.data?.message || 'No se pudo cargar el perfil.';
              toast.error(errorMessage);
              setSlice('profiles', { isLoading: false, error: errorMessage });
              throw error;
            }
          },

          clearProfileFromCache: (username) => {
            set((state) => {
              const newCache = { ...state.profiles.profilesCache };
              if (newCache[username.toLowerCase()]) {
                delete newCache[username.toLowerCase()];
              }
              return { profiles: { ...state.profiles, profilesCache: newCache } };
            });
          },

          updatePostInProfile: (username, updatedPost) => {
            set((state) => {
              const lowerCaseUsername = username.toLowerCase();
              const profile = state.profiles.profilesCache[lowerCaseUsername];
              if (!profile) return {};

              const updatedPagination = {
                ...profile.pagination,
                docs: profile.pagination.docs.map((p) =>
                  p._id === updatedPost._id ? updatedPost : p
                ),
              };

              return {
                profiles: {
                  ...state.profiles,
                  profilesCache: {
                    ...state.profiles.profilesCache,
                    [lowerCaseUsername]: {
                      ...profile,
                      pagination: updatedPagination,
                    },
                  },
                },
              };
            });
          },

          updateProfileDataInCache: (username, dataToUpdate) => {
            set((state) => {
              const lowerCaseUsername = username.toLowerCase();
              const profile = state.profiles.profilesCache[lowerCaseUsername];
              if (!profile) return {};

              const newProfileData = { ...profile, ...dataToUpdate };
              if (dataToUpdate.user && profile.user) {
                newProfileData.user = { ...profile.user, ...dataToUpdate.user };
              }

              return {
                profiles: {
                  ...state.profiles,
                  profilesCache: {
                    ...state.profiles.profilesCache,
                    [lowerCaseUsername]: newProfileData,
                  },
                },
              };
            });
          },

          toggleFollow: async (username) => {
            const profile = get().profiles.profilesCache[username.toLowerCase()];
            if (!profile) return;

            const isFollowing = profile.user.isFollowing;
            const endpoint = isFollowing ? `/users/unfollow/${profile.user._id}` : `/users/follow/${profile.user._id}`;
            const method = 'post';

            const newFollowersCount = Math.max(0, (profile.user.followersCount || 0) + (isFollowing ? -1 : 1));

            const previousProfile = { ...profile };

            set((state) => ({
              profiles: {
                ...state.profiles,
                profilesCache: {
                  ...state.profiles.profilesCache,
                  [username.toLowerCase()]: {
                    ...profile,
                    user: { ...profile.user, isFollowing: !isFollowing, followersCount: newFollowersCount },
                  },
                },
              },
            }));

            try {
              await apiClient[method](endpoint);

              try {
                useInteractionStore.getState().fetchFriendsData();
              } catch (err) {
                console.warn('[useDataStore] InteractionStore sync:', err);
              }

              const socket = getSocket();
              if (socket) {
                const action = isFollowing ? 'unfollow' : 'follow';
                socket.emit('user-follow-action', {
                  action,
                  targetUserId: profile.user._id,
                  followerUsername: username,
                });
              }
            } catch (error) {
              toast.error('No se pudo completar la acción.');
              set((state) => ({
                profiles: {
                  ...state.profiles,
                  profilesCache: {
                    ...state.profiles.profilesCache,
                    [username.toLowerCase()]: previousProfile,
                  },
                },
              }));
            }
          },

          updateAvatar: async (payload) => {
            try {
              const response = await apiClient.patch('/media/avatar/update', payload);
              const username = response.data.user.username;
              const updatedUser = response.data.user;
              const updatedUserId = updatedUser?._id || updatedUser?.id;
              const payloadMedia = normalizeMediaObject(payload?.avatar || payload?.avatarUrl);
              const responseMedia = normalizeMediaObject(updatedUser?.avatar);
              const nextAvatar = payloadMedia || responseMedia;
              const nextUser = { ...updatedUser, avatar: nextAvatar };
              const nextAvatarUrl = resolveMediaUrl(nextAvatar) || updatedUser?.avatar;

              get().updateProfileDataInCache(username, { user: nextUser });
              const authState = useAuthStore.getState();
              if (authState.currentUser && response.data.user?._id === authState.currentUser._id) {
                authState.setCurrentUser({ ...authState.currentUser, avatar: nextAvatar });
              }

              if (updatedUserId) {
                set((state) => {
                  const updatePostAvatar = (post) => {
                    if (!post || !post.author) return post;
                    const authorId = post.author._id || post.author.id;
                    if (authorId !== updatedUserId) return post;
                    return {
                      ...post,
                      author: { ...post.author, avatar: nextAvatarUrl },
                    };
                  };

                  const updatedPosts = state.feed.posts.map(updatePostAvatar);
                  const updatedWallPosts = Object.fromEntries(
                    Object.entries(state.feed.wallPosts).map(([userId, posts]) => [
                      userId,
                      posts.map(updatePostAvatar),
                    ])
                  );

                  return {
                    feed: {
                      ...state.feed,
                      posts: updatedPosts,
                      wallPosts: updatedWallPosts,
                    },
                  };
                });
              }
              toast.success('Avatar actualizado.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'Error al actualizar el avatar.');
              return { success: false };
            }
          },

          updateCoverPhoto: async (payload) => {
            try {
              const response = await apiClient.patch('/media/cover/update', payload);
              const username = response.data.user.username;
              const updatedUser = response.data.user;
              const payloadMedia = normalizeMediaObject(payload?.coverPhoto || payload?.coverPhotoUrl);
              const responseMedia = normalizeMediaObject(updatedUser?.coverPhoto);
              const nextCover = payloadMedia || responseMedia;
              const nextUser = { ...updatedUser, coverPhoto: nextCover };
              get().updateProfileDataInCache(username, { user: nextUser });
              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser({ ...authState.currentUser, coverPhoto: nextCover });
              }
              toast.success('Foto de portada actualizada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'Error al actualizar la portada.');
              return { success: false };
            }
          },

          updateAvatarFromAlbum: async (photoUrl) => {
            setSlice('profiles', { isUpdatingAvatar: true });
            try {
              const resolvedPhotoUrl = typeof photoUrl === 'string'
                ? photoUrl
                : (photoUrl?.url || photoUrl?.path || '');

              if (!resolvedPhotoUrl || !resolvedPhotoUrl.includes('/')) {
                toast.error('URL de foto inválida para el avatar.');
                setSlice('profiles', { isUpdatingAvatar: false });
                return { success: false };
              }

              const response = await apiClient.patch('/users/profile/update-avatar-from-album', { photoUrl: resolvedPhotoUrl });
              const username = response.data.user.username;
              const updatedUser = response.data.user;
              const nextAvatar = normalizeMediaObject(updatedUser?.avatar) || normalizeMediaObject(resolvedPhotoUrl);
              const nextUser = { ...updatedUser, avatar: nextAvatar };
              get().updateProfileDataInCache(username, { user: nextUser });
              const authState = useAuthStore.getState();
              if (authState.currentUser) {
                authState.setCurrentUser({ ...authState.currentUser, avatar: nextAvatar });
              }
              toast.success(response.data.message || 'Avatar actualizado.');
              setSlice('profiles', { isUpdatingAvatar: false });
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'Error al actualizar el avatar.');
              setSlice('profiles', { isUpdatingAvatar: false });
              return { success: false };
            }
          },

          updateCoverFromAlbum: async (photoUrl) => {
            setSlice('profiles', { isUpdatingCover: true });
            try {
              const resolvedPhotoUrl = typeof photoUrl === 'string'
                ? photoUrl
                : (photoUrl?.url || photoUrl?.path || '');

              if (!resolvedPhotoUrl || !resolvedPhotoUrl.includes('/')) {
                toast.error('URL de foto inválida para la portada.');
                setSlice('profiles', { isUpdatingCover: false });
                return { success: false };
              }

              const response = await apiClient.patch('/users/profile/update-cover-from-album', { photoUrl: resolvedPhotoUrl });
              const username = response.data.user.username;
              const updatedUser = response.data.user;
              const nextCover = normalizeMediaObject(updatedUser?.coverPhoto) || normalizeMediaObject(resolvedPhotoUrl);
              const nextUser = { ...updatedUser, coverPhoto: nextCover };
              get().updateProfileDataInCache(username, { user: nextUser });
              const authState = useAuthStore.getState();
              if (authState.currentUser) {
                authState.setCurrentUser({ ...authState.currentUser, coverPhoto: nextCover });
              }
              toast.success(response.data.message || 'Foto de portada actualizada.');
              setSlice('profiles', { isUpdatingCover: false });
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'Error al actualizar la portada.');
              setSlice('profiles', { isUpdatingCover: false });
              return { success: false };
            }
          },

          removePhoto: async (username, photoId) => {
            try {
              const response = await apiClient.delete(`/albums/photos/${photoId}`);
              toast.success(response.data.message || 'Foto eliminada.');
              return { success: true, album: response.data.album };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo eliminar la foto.');
              return { success: false };
            }
          },

          addClip: async (payload) => {
            try {
              const response = await apiClient.post('/users/profile/add-clip', payload);
              const updatedUser = response.data.user;
              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }
              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }
              toast.success(response.data.message || 'Clip añadido con éxito.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo añadir el clip.');
              return { success: false };
            }
          },

          removeClip: async (clipId) => {
            try {
              const response = await apiClient.delete(`/users/profile/remove-clip/${clipId}`);
              const updatedUser = response.data.user;
              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }
              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }
              toast.success(response.data.message || 'Clip eliminado con éxito.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo eliminar el clip.');
              return { success: false };
            }
          },

          addSongs: async (songs) => {
            try {
              const response = await apiClient.patch('/users/profile/add-music', { songs });
              const updatedUser = response.data.user;
              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }
              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }
              toast.success(response.data.message || 'Música agregada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo agregar música.');
              return { success: false };
            }
          },

          removeSong: async (songId) => {
            try {
              const response = await apiClient.delete(`/users/profile/remove-music/${songId}`);
              const updatedUser = response.data.user;
              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }
              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }
              toast.success(response.data.message || 'Canción eliminada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo eliminar la canción.');
              return { success: false };
            }
          },

          searchMusic: async (query) => {
            try {
              const response = await apiClient.get(`/music/search?q=${encodeURIComponent(query)}`);
              return response.data;
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo buscar música.');
              return null;
            }
          },

          updatePrivacy: async (privacySettings) => {
            try {
              const response = await apiClient.patch('/users/profile/privacy', privacySettings);
              const updatedUser = response.data.user || response.data;
              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }
              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }
              toast.success(response.data.message || 'Privacidad actualizada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo actualizar la privacidad.');
              return { success: false };
            }
          },

          updateUserProfile: async (data) => {
            try {
              const response = await apiClient.put('/users/profile', data);
              const updatedUser = response.data.user || response.data;
              const profileData = response.data.profileData;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, {
                  user: updatedUser,
                  profileData,
                });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Perfil actualizado.');
              return { success: true, user: updatedUser, profileData };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo actualizar el perfil.');
              return { success: false };
            }
          },

          changePassword: async (passwords) => {
            try {
              const response = await apiClient.patch('/users/profile/change-password', passwords);
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Contraseña actualizada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo actualizar la contraseña.');
              return { success: false };
            }
          },

          toggleBiometric: async () => {
            try {
              const response = await apiClient.patch('/users/profile/toggle-biometric');
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Configuración biométrica actualizada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo actualizar biometría.');
              return { success: false };
            }
          },

          scheduleAccountDeletion: async (data) => {
            try {
              const response = await apiClient.post('/users/profile/delete-account', data);
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Cuenta programada para eliminación.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo programar la eliminación.');
              return { success: false };
            }
          },

          cancelAccountDeletion: async () => {
            try {
              const response = await apiClient.post('/users/profile/cancel-deletion');
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Eliminación cancelada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo cancelar la eliminación.');
              return { success: false };
            }
          },

          requestVerification: async () => {
            try {
              const response = await apiClient.post('/users/request-verification');
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Verificación solicitada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo solicitar la verificación.');
              return { success: false };
            }
          },

          activateCreatorTools: async (payload = {}) => {
            try {
              const response = await apiClient.post('/users/profile/activate-creator-tools', payload);
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Herramientas activadas.');
              return {
                success: true,
                user: updatedUser,
                status: response.data.status,
                requiresIdentitySelection: !!response.data.requiresIdentitySelection,
                reviewWindowHours: response.data.reviewWindowHours || null,
              };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo activar herramientas.');
              return { success: false };
            }
          },

          generateTwoFactorSecret: async () => {
            try {
              const response = await apiClient.post('/users/profile/2fa/generate');
              return { success: true, qrCodeUrl: response.data.qrCodeUrl };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo generar el secreto 2FA.');
              return { success: false };
            }
          },

          verifyAndEnableTwoFactor: async (token) => {
            try {
              const response = await apiClient.post('/users/profile/2fa/verify', { token });
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Verificación en 2 pasos activada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'El código no es válido.');
              return { success: false };
            }
          },

          disableTwoFactor: async () => {
            try {
              const response = await apiClient.post('/users/profile/2fa/disable');
              const updatedUser = response.data.user || response.data;

              if (updatedUser?.username) {
                get().updateProfileDataInCache(updatedUser.username, { user: updatedUser });
              }

              const authState = useAuthStore.getState();
              if (authState.currentUser && updatedUser?._id === authState.currentUser._id) {
                authState.setCurrentUser(updatedUser);
              }

              toast.success(response.data.message || 'Verificación en 2 pasos desactivada.');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo desactivar 2FA.');
              return { success: false };
            }
          },

          getDeviceHistory: async () => {
            try {
              const response = await apiClient.get('/users/profile/devices');
              return response.data || [];
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo cargar el historial de dispositivos.');
              return [];
            }
          },

          logoutDevice: async (sessionId) => {
            try {
              const response = await apiClient.delete(`/users/profile/devices/${sessionId}`);
              toast.success(response.data.message || 'Sesión cerrada con éxito.');
              return { success: true, activeSessions: response.data.activeSessions };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo cerrar la sesión.');
              return { success: false };
            }
          },

          removeFriend: async (friendId) => {
            try {
              await apiClient.delete(`/users/remove-friend/${friendId}`);
              toast.success('Amigo eliminado');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo eliminar al amigo.');
              return { success: false };
            }
          },

          updateNotificationSettings: async (settings) => {
            try {
              await apiClient.put('/users/notification-settings', settings);
              toast.success('Configuración de notificaciones actualizada');
              return { success: true };
            } catch (error) {
              toast.error(error.response?.data?.message || 'No se pudo actualizar la configuración de notificaciones.');
              return { success: false };
            }
          },

          resetProfiles: () => setSlice('profiles', initialState.profiles),

          // =========================
          // Media (devices/stream)
          // =========================
          _setStream: (stream) => {
            const oldStream = get().media.stream;
            if (oldStream) {
              oldStream.getTracks().forEach(track => track.stop());
            }
            set((state) => ({
              media: { ...state.media, stream, isStreamActive: !!stream, error: null },
            }));
          },

          enumerateDevices: async () => {
            try {
              await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
              const devices = await navigator.mediaDevices.enumerateDevices();
              const videoDevices = devices.filter(d => d.kind === 'videoinput');
              const audioDevices = devices.filter(d => d.kind === 'audioinput');
              set((state) => ({
                media: { ...state.media, videoDevices, audioDevices, error: null },
              }));

              if (!get().media.selectedVideoId && videoDevices.length > 0) {
                set((state) => ({ media: { ...state.media, selectedVideoId: videoDevices[0].deviceId } }));
              }
              if (!get().media.selectedAudioId && audioDevices.length > 0) {
                set((state) => ({ media: { ...state.media, selectedAudioId: audioDevices[0].deviceId } }));
              }
            } catch (err) {
              console.error('Error enumerating devices:', err);
              set((state) => ({ media: { ...state.media, error: 'Permission denied. Please allow camera and microphone access.' } }));
            }
          },

          selectDevices: ({ videoId, audioId }) => {
            set((state) => ({ media: { ...state.media, selectedVideoId: videoId, selectedAudioId: audioId } }));
            if (get().media.isStreamActive) {
              get().startStream();
            }
          },

          startStream: async () => {
            const { currentUser } = useAuthStore.getState();
            if (!['blogger', 'admin', 'admin_root', 'moderator', 'technician', 'editor'].includes(currentUser?.role)) {
              set((state) => ({ media: { ...state.media, error: "You don't have permission to start a stream." } }));
              return;
            }

            const { selectedVideoId, selectedAudioId } = get().media;
            const constraints = {
              video: { deviceId: { exact: selectedVideoId } },
              audio: { deviceId: { exact: selectedAudioId } },
            };

            try {
              const stream = await navigator.mediaDevices.getUserMedia(constraints);
              get()._setStream(stream);
            } catch (err) {
              console.error('Error starting stream:', err);
              set((state) => ({ media: { ...state.media, error: 'Could not start stream with selected devices.' } }));
              get()._setStream(null);
            }
          },

          stopStream: () => {
            get()._setStream(null);
          },
        };
      },
      {
        name: 'data-store',
        partialize: (state) => ({
          media: {
            selectedVideoId: state.media.selectedVideoId,
            selectedAudioId: state.media.selectedAudioId,
          },
        }),
      }
    )
  )
);

useAuthStore.subscribe(
  (isLoggedIn) => {
    if (!isLoggedIn) {
      useDataStore.getState().stopStream();
    }
  },
  (state) => state.isLoggedIn
);

const applySelector = (selector, slice) =>
  typeof selector === 'function' ? selector(slice) : slice;

export const useArticlesStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      relatedCache: state.articles.relatedCache,
      navigationCache: state.articles.navigationCache,
      articlesCache: state.articles.articlesCache,
      news: state.articles.news,
      isLoading: state.articles.isLoading,
      error: state.articles.error,
      getArticleById: state.getArticleById,
      createArticle: state.createArticle,
      updateArticle: state.updateArticle,
      deleteArticle: state.deleteArticle,
      fetchRelatedArticles: state.fetchRelatedArticles,
      fetchNews: state.fetchNews,
      uploadArticleImage: state.uploadArticleImage,
      fetchArticlesByAuthor: state.fetchArticlesByAuthor,
      fetchArticleNavigation: state.fetchArticleNavigation,
      incrementPostView: state.incrementPostView,
    })
  );

export const useFeedStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      posts: state.feed.posts,
      wallPosts: state.feed.wallPosts,
      isLoading: state.feed.isLoading,
      page: state.feed.page,
      hasMore: state.feed.hasMore,
      authUserId: state.feed.authUserId,
      authUsername: state.feed.authUsername,
      authUserAvatar: state.feed.authUserAvatar,
      fetchFeed: state.fetchFeed,
      loadMorePosts: state.loadMorePosts,
      fetchWallPosts: state.fetchWallPosts,
      addPost: state.addPost,
      forwardPost: state.forwardPost,
      deletePost: state.deletePost,
      updatePost: state.updatePost,
      updatePostPrivacy: state.updatePostPrivacy,
      likePost: state.likePost,
      addComment: state.addComment,
      likeComment: state.likeComment,
      addReplyToComment: state.addReplyToComment,
      setAuthUser: state.setAuthUser,
    })
  );

export const useStoriesStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      stories: state.stories.stories,
      archivedStories: state.stories.archivedStories,
      isLoading: state.stories.isLoading,
      isLoadingArchived: state.stories.isLoadingArchived,
      fetchStories: state.fetchStories,
      addStory: state.addStory,
      fetchArchivedStories: state.fetchArchivedStories,
      archiveStory: state.archiveStory,
      likeStory: state.likeStory,
      deleteStory: state.deleteStory,
    })
  );

export const useJobsStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      jobs: state.jobs.jobs,
      currentJob: state.jobs.currentJob,
      isLoading: state.jobs.isLoading,
      isApplying: state.jobs.isApplying,
      error: state.jobs.error,
      fetchJobs: state.fetchJobs,
      fetchJobById: state.fetchJobById,
      applyToJob: state.applyToJob,
      saveJobAlert: state.saveJobAlert,
      createJob: state.createJob,
    })
  );

export const usePollStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      polls: state.polls.polls,
      selectedPoll: state.polls.selectedPoll,
      isLoading: state.polls.isLoading,
      error: state.polls.error,
      userVotes: state.polls.userVotes,
      fetchPolls: state.fetchPolls,
      fetchPoll: state.fetchPoll,
      voteOnPoll: state.voteOnPoll,
      createPoll: state.createPoll,
      closePoll: state.closePoll,
      setSelectedPoll: state.setSelectedPoll,
      reset: state.resetPolls,
      subscribeToPolls: state.subscribeToPolls,
    })
  );

export const useSurveyStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      surveyCache: state.surveys.surveyCache,
      isLoading: state.surveys.isLoading,
      isSubmitting: state.surveys.isSubmitting,
      error: state.surveys.error,
      fetchSurvey: state.fetchSurvey,
      submitSurveyResponse: state.submitSurveyResponse,
    })
  );

export const usePublicAdsStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      ads: state.publicAds.ads,
      isLoading: state.publicAds.isLoading,
      error: state.publicAds.error,
      fetchPublicAds: state.fetchPublicAds,
      clearAds: state.clearAds,
    })
  );

export const useDefaultAssetsStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      assets: state.defaultAssets.assets,
      isLoading: state.defaultAssets.isLoading,
      error: state.defaultAssets.error,
      fetchDefaultAssets: state.fetchDefaultAssets,
    })
  );

export const useUserAdsStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      myAds: state.userAds.myAds,
      isLoading: state.userAds.isLoading,
      error: state.userAds.error,
      fetchMyAds: state.fetchMyAds,
      createAd: state.createAd,
    })
  );

export const useCategoriesStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      categories: state.categories.categories,
      isLoading: state.categories.isLoading,
      error: state.categories.error,
      fetchCategories: state.fetchCategories,
    })
  );

export const useStoresStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      stores: state.stores.stores,
      storesCache: state.stores.storesCache,
      isLoading: state.stores.isLoading,
      error: state.stores.error,
      fetchStoreByName: state.fetchStoreByName,
      fetchStores: state.fetchStores,
    })
  );

export const useBloggersStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      bloggers: state.bloggers.bloggers,
      isLoading: state.bloggers.isLoading,
      error: state.bloggers.error,
      fetchBloggers: state.fetchBloggers,
    })
  );

export const useCampaignsStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      campaigns: state.campaigns.campaigns,
      isLoading: state.campaigns.isLoading,
      currentCampaignDetails: state.campaigns.currentCampaignDetails,
      error: state.campaigns.error,
      fetchCampaignsByPage: state.fetchCampaignsByPage,
      createCampaign: state.createCampaign,
      fetchCampaignDetails: state.fetchCampaignDetails,
    })
  );

export const useCompanyStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      companyCache: state.company.companyCache,
      company: state.company.company,
      isLoading: state.company.isLoading,
      isSubmitting: state.company.isSubmitting,
      error: state.company.error,
      fetchCompanyByName: state.fetchCompanyByName,
      setCompanyField: state.setCompanyField,
      fetchCompanyById: state.fetchCompanyById,
      updateCompany: state.updateCompany,
      activateCompany: state.activateCompany,
      deactivateCompany: state.deactivateCompany,
      deleteCompany: state.deleteCompany,
      cancelCompanyDeletion: state.cancelCompanyDeletion,
      createJobForCompany: state.createJobForCompany,
    })
  );

export const useAlbumStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      isUploading: state.albums.isUploading,
      uploadProgress: state.albums.uploadProgress,
      collections: state.albums.collections,
      isLoading: state.albums.isLoading,
      fetchAlbumCollections: state.fetchAlbumCollections,
      fetchAlbumPhotos: state.fetchAlbumPhotos,
      addPhotosToAlbum: state.addPhotosToAlbum,
      updatePhotoVisibility: state.updatePhotoVisibility,
      updateAlbumVisibility: state.updateAlbumVisibility,
    })
  );

export const useClipStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      isUploading: state.clips.isUploading,
      uploadProgress: state.clips.uploadProgress,
      uploadClip: state.uploadClip,
      addClip: state.addClip,
      removeClip: state.removeClip,
    })
  );

export const useUsersStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      users: state.users.users,
      isLoading: state.users.isLoading,
      error: state.users.error,
      fetchUsers: state.fetchUsers,
      toggleFollowUser: state.toggleFollowUser,
    })
  );

export const useProfileStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      profilesCache: state.profiles.profilesCache,
      isLoading: state.profiles.isLoading,
      error: state.profiles.error,
      isUpdatingAvatar: state.profiles.isUpdatingAvatar,
      isUpdatingCover: state.profiles.isUpdatingCover,
      getProfile: state.getProfile,
      fetchProfile: state.fetchProfile,
      clearProfileFromCache: state.clearProfileFromCache,
      updatePostInProfile: state.updatePostInProfile,
      updateProfileDataInCache: state.updateProfileDataInCache,
      updateUserProfile: state.updateUserProfile,
      toggleFollow: state.toggleFollow,
      updateAvatar: state.updateAvatar,
      updateCoverPhoto: state.updateCoverPhoto,
      updateAvatarFromAlbum: state.updateAvatarFromAlbum,
      updateCoverFromAlbum: state.updateCoverFromAlbum,
      removePhoto: state.removePhoto,
      addPhotosToAlbum: state.addPhotosToAlbum,
      addClip: state.addClip,
      removeClip: state.removeClip,
      addSongs: state.addSongs,
      removeSong: state.removeSong,
      searchMusic: state.searchMusic,
      changePassword: state.changePassword,
      toggleBiometric: state.toggleBiometric,
      generateTwoFactorSecret: state.generateTwoFactorSecret,
      verifyAndEnableTwoFactor: state.verifyAndEnableTwoFactor,
      disableTwoFactor: state.disableTwoFactor,
      scheduleAccountDeletion: state.scheduleAccountDeletion,
      cancelAccountDeletion: state.cancelAccountDeletion,
      requestVerification: state.requestVerification,
      getDeviceHistory: state.getDeviceHistory,
      logoutDevice: state.logoutDevice,
      updatePrivacy: state.updatePrivacy,
      removeFriend: state.removeFriend,
      updateNotificationSettings: state.updateNotificationSettings,
      activateCreatorTools: state.activateCreatorTools,
      reset: state.resetProfiles,
    })
  );

export const useMediaStore = (selector) =>
  useDataStore((state) =>
    applySelector(selector, {
      videoDevices: state.media.videoDevices,
      audioDevices: state.media.audioDevices,
      selectedVideoId: state.media.selectedVideoId,
      selectedAudioId: state.media.selectedAudioId,
      stream: state.media.stream,
      isStreamActive: state.media.isStreamActive,
      error: state.media.error,
      enumerateDevices: state.enumerateDevices,
      selectDevices: state.selectDevices,
      startStream: state.startStream,
      stopStream: state.stopStream,
    })
  );
